/**
 * MenuBar right side: Spotlight | FLZ | Agent count | Clock/Date (tıklanabilir)
 * Saat tıklanınca Notification Center açılacak (A.4'te eklenecek)
 */
import { Bell, Moon, Search, Settings2, Sun, Zap } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useFocusMode } from '../../../contexts/FocusModeContext'
import { useOSTheme } from '../OSThemeContext'
import type { AgentStats } from './useMenuBarData'

interface Props {
  agentStats: AgentStats | null
  flzBalance: number
  onSpotlightOpen?: () => void
  onClockClick?: () => void
  onAlertClick?: () => void
  onControlCenterOpen?: () => void
}

export default function MenuBarStatus({ agentStats, flzBalance, onSpotlightOpen, onClockClick, onAlertClick, onControlCenterOpen }: Props) {
  const [time, setTime] = useState(new Date())
  const [alertCount, setAlertCount] = useState(0)
  const { theme, toggle } = useOSTheme()
  const { enabled: dndActive, toggle: toggleDND } = useFocusMode()

  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 30000)
    return () => clearInterval(t)
  }, [])

  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        const token = localStorage.getItem('token')
        if (!token) return
        const res = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:8888'}/api/user/alerts`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        if (res.ok) {
          const data = await res.json()
          setAlertCount(data.count ?? data.unread_count ?? 0)
        }
      } catch {
        // silent fail — endpoint may not exist yet
      }
    }
    fetchAlerts()
    const interval = setInterval(fetchAlerts, 60000)
    return () => clearInterval(interval)
  }, [])

  const timeStr = time.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })
  const dateStr = time.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short' })

  const iconBtn = (children: React.ReactNode, onClick?: () => void, title?: string) => (
    <button onClick={onClick} title={title} style={{
      background: 'transparent', border: 'none', cursor: 'pointer',
      color: 'var(--os-muted)', display: 'flex', alignItems: 'center',
      padding: '2px 4px', borderRadius: 4, transition: 'all 0.15s',
    }}
      onMouseEnter={e => { e.currentTarget.style.background = 'var(--os-hover)'; e.currentTarget.style.color = 'var(--os-text)' }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--os-muted)' }}
    >{children}</button>
  )

  return (
    <div style={{
      marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10,
      fontSize: 11, color: 'var(--os-muted)',
    }}>
      {/* Agent count */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#10b981', boxShadow: '0 0 5px #10b981' }} />
        <span>{agentStats?.active_agents ?? '—'}</span>
      </div>

      {/* FLZ */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 3, color: '#f59e0b', fontWeight: 600 }}>
        <Zap size={11} />
        <span>{flzBalance.toLocaleString('en-US', { maximumFractionDigits: 0 })}</span>
      </div>

      {/* Spotlight */}
      {iconBtn(<Search size={13} />, onSpotlightOpen, 'Spotlight (⌘K)')}

      {/* Focus Mode / DND */}
      {iconBtn(
        <Moon size={13} style={dndActive ? { color: 'var(--os-warning, #f59e0b)' } : undefined} />,
        toggleDND,
        dndActive ? 'Do Not Disturb (click to disable)' : 'Focus Mode'
      )}

      {/* Bell — Notifications */}
      <div style={{ position: 'relative' }}>
        {iconBtn(
          <Bell size={13} />,
          onAlertClick,
          'Notifications'
        )}
        {alertCount > 0 && (
          <div style={{
            position: 'absolute',
            top: -4,
            right: -6,
            background: '#ef4444',
            color: 'white',
            fontSize: 9,
            fontWeight: 700,
            borderRadius: '50%',
            width: 16,
            height: 16,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            {alertCount > 9 ? '9+' : alertCount}
          </div>
        )}
      </div>

      {/* Theme toggle */}
      {iconBtn(
        theme === 'dark' ? <Sun size={13} /> : <Moon size={13} />,
        toggle,
        theme === 'dark' ? 'Light Mode' : 'Dark Mode'
      )}

      {/* Control Center */}
      {iconBtn(<Settings2 size={13} />, onControlCenterOpen, 'Control Center')}

      {/* Clock — tıklanınca Notification Center (A.4) */}
      <button onClick={onClockClick} style={{
        background: 'transparent', border: 'none', cursor: 'pointer',
        color: 'var(--os-muted)', fontSize: 11, padding: '2px 6px',
        borderRadius: 4, fontFamily: 'inherit', transition: 'all 0.15s',
      }}
        onMouseEnter={e => { e.currentTarget.style.background = 'var(--os-hover)'; e.currentTarget.style.color = 'var(--os-text)' }}
        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--os-muted)' }}
      >
        {dateStr} {timeStr}
      </button>
    </div>
  )
}
